import { Box, Typography, Stack, Alert } from "@mui/material";
import { useState } from "react";
import { Spinner, Button } from "react-bootstrap";

import {
  useAddNoteMutation,
  useUpdateNoteMutation,
} from "api/memberPortal/notes/notes.api";
import { useAppForm } from "components/Forms/AppForm";
import DialogModal from "components/Forms/components/modals/DialogModal";
import { useAuth } from "hooks/useAuth";
import { getISOCurrentDate } from "utils/timeUtils";

import {
  ProductNoteFormProps,
  LabValidatorInternalNoteFormValues,
  LabValidatorInternalNoteFormSchema,
} from "./NoteForm.schema";

const ProductNoteForm = ({
  show,
  handleClose,
  note,
  product,
  replyTo,
}: ProductNoteFormProps) => {
  const { currentUser, permissions } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [triggerUpdateNote] = useUpdateNoteMutation();
  const [triggerAddNote] = useAddNoteMutation();

  const isNiap = permissions.role_type === "NIAP";
  const isLab = permissions.role_type === "Lab";
  const isValidator = permissions.role_type === "Validator";

  const isNiapValidatorOrLab = isNiap || isValidator || isLab;

  const productNoteForm = useAppForm({
    defaultValues: {
      internal_only: note?.internal_only ?? false,
      visible_to_labs: note?.visible_to_labs ?? false,
      visible_to_validators: note?.visible_to_validators ?? true,
      title: note?.title,
      note: note?.note,
    } as LabValidatorInternalNoteFormValues,
    validators: {
      onSubmit: LabValidatorInternalNoteFormSchema,
    },
    onSubmit: async ({ value }) => {
      setError(null);
      setLoading(true);
      try {
        if (note?.note_id) {
          await triggerUpdateNote({
            noteId: note.note_id,
            body: {
              ...value,
            },
          });
        } else {
          await triggerAddNote({
            ...value,
            note_type: "product",
            note_type_id: product.product_id,
            visible_to_smes: false,
            in_response_to: replyTo?.note_id,
            submitted_on: getISOCurrentDate(),
          });
        }
        handleClose();
        setLoading(false);
      } catch (error) {
        console.error(error);
        setError("Error adding note. Please try again.");
        setLoading(false);
      }
    },
  });

  return (
    <DialogModal
      title={`+ NOTE for VID ${product.v_id}`}
      open={show}
      maxWidth="md"
      fullWidth
      slotProps={{
        backdrop: {
          timeout: 300,
        },
      }}
    >
      <Box>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            productNoteForm.handleSubmit();
          }}
        >
          <Typography id="modal-modal-description" variant="body1">
            <Typography variant="body1" fontWeight="bold" className="mb-3">
              Submitter: {currentUser.username}
            </Typography>
            <Box className="mb-3">
              <Stack spacing={1}>
                {isNiapValidatorOrLab && (
                  <productNoteForm.AppField name="internal_only">
                    {/* we nest the visible_to_labs and visible_to_validators under the internal_only context to show or hide them based on the internal_only value */}
                    {(internalOnlyField) => (
                      <>
                        <internalOnlyField.YesNoRadioButton
                          label="Is Private?"
                          isHorizontal={true}
                        />
                        {!isLab && !internalOnlyField.state.value && (
                          <productNoteForm.AppField name="visible_to_labs">
                            {(labsField) => (
                              <labsField.YesNoRadioButton
                                label="Visible To Labs?"
                                isHorizontal={true}
                              />
                            )}
                          </productNoteForm.AppField>
                        )}
                        {!isValidator && !internalOnlyField.state.value && (
                          <productNoteForm.AppField name="visible_to_validators">
                            {(validatorsField) => (
                              <validatorsField.YesNoRadioButton
                                label="Visible To Validators?"
                                isHorizontal={true}
                              />
                            )}
                          </productNoteForm.AppField>
                        )}
                      </>
                    )}
                  </productNoteForm.AppField>
                )}
              </Stack>
            </Box>
            <Stack spacing={3}>
              <productNoteForm.AppField name="title">
                {(field) => <field.TextInput label="Title" />}
              </productNoteForm.AppField>

              <productNoteForm.AppField name="note">
                {(field) => <field.HtmlEditor label="Overview" />}
              </productNoteForm.AppField>
            </Stack>
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              p: 2,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Button variant="secondary" onClick={() => handleClose()}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={loading}
              type={loading ? "button" : "submit"}
              data-cy="submit-note"
            >
              {loading && <Spinner animation="border" size="sm" />}
              {note?.note_id ? "Update" : "Add"}
            </Button>
          </Box>
          {error && (
            <Alert severity="error" sx={{ mb: 2, width: "100%" }}>
              {error}
            </Alert>
          )}
        </form>
      </Box>
    </DialogModal>
  );
};

export default ProductNoteForm;
